/**
 * Broken-image fallback for `<img>` previews. When the source 404s or otherwise
 * fails to load, swap to a committed placeholder asset and mark the image as
 * unavailable, instead of showing the browser's broken-image glyph.
 *
 * @module
 */

/** Committed placeholder shown when an image fails to load. */
export const IMAGE_FALLBACK_SRC = '/uploads/_placeholder.svg';

/** Accessible status suffix/label used when an image has failed to load. */
const UNAVAILABLE_STATUS = 'Image unavailable';

/**
 * Set an image's `alt` to `base`, keeping the "(image unavailable)" status
 * whenever the image is flagged failed (`data-image-unavailable`). Use this for
 * EVERY alt write on a fallback-wired image (e.g. a live title edit) so the
 * accessible failure status can't be silently erased. When the image has not
 * failed, the alt is just `base`.
 *
 * @param {HTMLImageElement} img
 * @param {string} base - The meaningful alt (e.g. the photo title), may be empty.
 * @returns {void}
 */
export function setImageAlt(img, base) {
  const trimmed = (base || '').trim();
  if (img.dataset.imageUnavailable === 'true') {
    img.alt = trimmed && trimmed !== UNAVAILABLE_STATUS
      ? `${trimmed} (image unavailable)`
      : UNAVAILABLE_STATUS;
  } else {
    img.alt = base;
  }
}

/**
 * Wire a ONE-SHOT error handler on an image element. On the first load error the
 * handler clears itself BEFORE reassigning `src` to the placeholder, so a missing
 * or also-broken placeholder cannot recurse infinitely.
 *
 * Accessibility: the failure is surfaced to assistive tech regardless of whether
 * the image already had an `alt` — the status is APPENDED ("… (image unavailable)")
 * rather than only filling an empty alt, since both driver call sites assign a
 * meaningful alt (the photo title) which would otherwise mask the failure. Once
 * flagged, callers must route further alt writes through `setImageAlt` so the
 * status survives a live title edit.
 *
 * Call this BEFORE assigning the real `src` (an `onerror` set after the error has
 * already fired would never run).
 *
 * @param {HTMLImageElement} img
 * @param {string} [fallbackSrc] - Override placeholder (defaults to IMAGE_FALLBACK_SRC).
 * @returns {void}
 */
export function attachImageFallback(img, fallbackSrc = IMAGE_FALLBACK_SRC) {
  img.onerror = () => {
    img.onerror = null; // one-shot: prevent infinite recursion if the placeholder also fails
    img.dataset.imageUnavailable = 'true';
    setImageAlt(img, img.alt); // re-compose the existing alt with the status suffix
    img.src = fallbackSrc;
  };
}
